import { z } from 'zod';
import { PAINT_TONES, type PaintTone } from '../domain/paint-tones';
import { PAINT_CATEGORIES, type PaintCategory } from '../domain/paint-categories';
import { PAINT_BRANDS, type PaintBrand } from '../domain/paint-brands';
import { LabColorSchema } from './decors';
import { PaginatedResponseSchema, PaginationQuerySchema } from './pagination';
import { RalCodeSchema } from './paint-shared';

/**
 * Контракты Block 3 «Краски» (PRD §6, ADR-0010).
 *
 * Соответствие источникам:
 *   - CSV `<BASE_PATH>/Краски/Фасады краска Ралы фото Готов.csv` (RAL classic);
 *   - PRD §6 — UX каталога, эндпоинты `/api/paint/*`;
 *   - ADR-0010 §1 — один paint-домен без внутреннего kind-discriminator;
 *   - ADR-0010 §2 — data model: 426 SKU = 426 verbatim Global_ID (Option B).
 *
 * P-001 enforcement:
 *   - `globalId: z.string().min(1)` — никаких regex / transform / normalize.
 *     CSV содержит `.jpg`-суффикс в Global_ID (например
 *     `Зелено-бежевый_Желтые тона_Кухня_Ral 1000.jpg`) — сохраняется как есть.
 *   - `imageFilename` — отдельное derived поле
 *     (`globalId.replace(/\.jpg$/, '.webp')` в адаптере), не модификация
 *     Global_ID. ADR-0010 §4.
 *
 * P-033 неприменимо: один paint kind, нет cross-kind утечки фильтров.
 */

// ── Vocabulary enums ─────────────────────────────────────────────────────
// Type-coercion `as unknown as [T, ...T[]]` — паттерн `decors.ts:48-52`
// для конвертации `Object.freeze([...] as const)` в `z.enum`.

const paintToneValues = PAINT_TONES as unknown as [PaintTone, ...PaintTone[]];
export const PaintToneSchema = z.enum(paintToneValues);

const paintCategoryValues = PAINT_CATEGORIES as unknown as [PaintCategory, ...PaintCategory[]];
export const PaintCategorySchema = z.enum(paintCategoryValues);

const paintBrandValues = PAINT_BRANDS as unknown as [PaintBrand, ...PaintBrand[]];
export const PaintBrandSchema = z.enum(paintBrandValues);

// ── RAL-код ──────────────────────────────────────────────────────────────
// `RalCodeSchema` извлечён в `paint-shared.ts` (B-039 closed, Phase 2 Шаг 0):
// 3-й consumer (`apiClient.paint`) импортирует ту же схему. Symmetry с
// `decor-match.ts → decors.ts` (контракт-родитель не зависит от потомков).

// ── Paint schema ─────────────────────────────────────────────────────────
// Один Paint = одна строка CSV (Option B per ADR-0010 §2). Без внутреннего
// kind-discriminator (ADR-0010 §1).
export const PaintSchema = z.object({
  globalId: z.string().min(1), // P-001: verbatim, может содержать `.jpg` в конце
  name: z.string().min(1), // CSV колонка `Цвет` (trailing-space header чистится parseCsv)
  ralCode: RalCodeSchema, // CSV колонка `Код RAL`
  tone: PaintToneSchema,
  category: PaintCategorySchema,
  brand: PaintBrandSchema,
  imageFilename: z.string().min(1), // `.webp` имя (derived из globalId, ADR-0010 §4)
  dominantLab: LabColorSchema.optional(), // вычисляется compute-paint-lab.ts (Шаг 5)
});
export type Paint = z.infer<typeof PaintSchema>;

// ── Облегчённая list-item схема ──────────────────────────────────────────
// Каталог Phase 3 рендерит swatch + name + RAL-код. Полная Paint-схема
// избыточна для list endpoint. Аналог `DecorListItemSchema` (decors.ts:124-136).
export const PaintListItemSchema = z.object({
  globalId: z.string().min(1),
  name: z.string().min(1),
  ralCode: RalCodeSchema,
  tone: PaintToneSchema,
  category: PaintCategorySchema,
  brand: PaintBrandSchema,
  primaryPhotoKey: z.string().nullable(),
  dominantLab: LabColorSchema.optional(),
});
export type PaintListItem = z.infer<typeof PaintListItemSchema>;

export const PaintListResponseSchema = PaginatedResponseSchema(PaintListItemSchema);
export type PaintListResponse = z.infer<typeof PaintListResponseSchema>;

// ── Filter schema ────────────────────────────────────────────────────────
// Один paint-домен → одна filter-функция (нет P-033 multi-kind проблемы,
// см. ADR-0010 §1).
export const PaintFilterSchema = z.object({
  tones: z.array(PaintToneSchema).optional(),
  categories: z.array(PaintCategorySchema).optional(),
  brands: z.array(PaintBrandSchema).optional(),
  ralCodes: z.array(RalCodeSchema).optional(),
});
export type PaintFilter = z.infer<typeof PaintFilterSchema>;

// ── List query (filter + pagination) ─────────────────────────────────────
export const PaintListQuerySchema = PaintFilterSchema.merge(PaginationQuerySchema);
export type PaintListQuery = z.infer<typeof PaintListQuerySchema>;
